// ThemeContext.js - Theme system with persistence
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

const THEME_STORAGE_KEY = "theme_preference";

const ThemeContext = createContext({
  theme: "light",
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState("light");
  const [isLoaded, setIsLoaded] = useState(false);

  // Apply theme to DOM (web only)
  const applyThemeToDOM = useCallback((themeValue) => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", themeValue);
      // Also set a CSS class for components that may need it
      document.body.classList.remove("theme-light", "theme-dark");
      document.body.classList.add(`theme-${themeValue}`);
    }
  }, []);

  // Load saved theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const savedTheme = await AsyncStorage.getItem(THEME_STORAGE_KEY);
        if (savedTheme && (savedTheme === "light" || savedTheme === "dark")) {
          setThemeState(savedTheme);
          applyThemeToDOM(savedTheme);
        } else {
          // Default to light and apply it
          applyThemeToDOM("light");
        }
      } catch (error) {
        console.error("Error loading theme preference:", error);
        applyThemeToDOM("light");
      } finally {
        setIsLoaded(true);
      }
    };
    loadTheme();
  }, [applyThemeToDOM]);

  // Persist and apply theme changes
  const setTheme = useCallback(async (newTheme) => {
    if (newTheme !== "light" && newTheme !== "dark") return;

    setThemeState(newTheme);
    applyThemeToDOM(newTheme);

    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (error) {
      console.error("Error saving theme preference:", error);
    }
  }, [applyThemeToDOM]);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  }, [theme, setTheme]);

  const value = {
    theme,
    isDark: theme === "dark",
    toggleTheme,
    setTheme,
    isLoaded,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
